import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button, IconButton, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { Close as CloseIcon, WhatsApp as WhatsAppIcon, Email as EmailIcon, Logout as LogoutIcon } from '@mui/icons-material';
import { Toaster, toast } from 'react-hot-toast';
import { AppThemeProvider } from './context/ThemeContext';
import { Layout } from './components/Layout';
import { TabType } from './types';
import { AppProvider, useAppContext } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { requestNotificationPermission } from './utils/helpers';
import { SubscriptionStatus } from './types/saas';
import { isAdmin } from './config/adminConfig';
import logger from './utils/logger';

// Lazy loaded pages — her sayfa ayrı JS chunk'ına bölünür
const BugunPage = lazy(() => import('./pages/BugunPage').then(m => ({ default: m.BugunPage })));
const TamamlanmisPage = lazy(() => import('./pages/TamamlanmisPage').then(m => ({ default: m.TamamlanmisPage })));
const OdemelerPage = lazy(() => import('./pages/OdemelerPage').then(m => ({ default: m.OdemelerPage })));
const AlacaklarPage = lazy(() => import('./pages/AlacaklarPage').then(m => ({ default: m.AlacaklarPage })));
const KomisyonPage = lazy(() => import('./pages/KomisyonPage').then(m => ({ default: m.KomisyonPage })));
const FaturaPage = lazy(() => import('./pages/FaturaPage').then(m => ({ default: m.FaturaPage })));
const KazancPage = lazy(() => import('./pages/KazancPage').then(m => ({ default: m.KazancPage })));
const GiderlerPage = lazy(() => import('./pages/GiderlerPage').then(m => ({ default: m.GiderlerPage })));
const SoforlerPage = lazy(() => import('./pages/SoforlerPage').then(m => ({ default: m.SoforlerPage })));
const FiyatListesiPage = lazy(() => import('./pages/FiyatListesiPage').then(m => ({ default: m.FiyatListesiPage })));
const YetkililerPage = lazy(() => import('./pages/YetkililerPage').then(m => ({ default: m.YetkililerPage })));
const FirmalarPage = lazy(() => import('./pages/FirmalarPage').then(m => ({ default: m.FirmalarPage })));
const KDVHesaplamaPage = lazy(() => import('./pages/KDVHesaplamaPage').then(m => ({ default: m.KDVHesaplamaPage })));
const FisTasarimPageNew = lazy(() => import('./pages/FisTasarimPageNew'));
const AyarlarPage = lazy(() => import('./pages/AyarlarPage').then(m => ({ default: m.AyarlarPage })));
const PDFViewerPage = lazy(() => import('./pages/PDFViewerPage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const QuickSetupPage = lazy(() => import('./pages/QuickSetupPage'));
const TeamManagementPage = lazy(() => import('./pages/TeamManagementPage'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const RaporlamaPage = lazy(() => import('./pages/RaporlamaPage'));
const CRMPage = lazy(() => import('./pages/CRMPage'));
const OdemePlanPage = lazy(() => import('./pages/OdemePlanPage'));
const WhatsAppPage = lazy(() => import('./pages/WhatsAppPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));

const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
    <CircularProgress size={40} />
  </Box>
);


// Ana uygulama bileşeni
function MainApp() {
  const [currentTab, setCurrentTab] = useState<TabType>('bugun');
  const { setAddJobDialogOpen } = useAppContext();
  const { user, team, isLoading, subscriptionStatus, teamSubscriptionStatus, daysRemaining, logout, needsPhone, completePhoneSetup } = useAuth();
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneSubmitting, setPhoneSubmitting] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Uygulama başladığında bildirim izni iste
  useEffect(() => {
    const requestPermission = async () => {
      try {
        await requestNotificationPermission();
      } catch (error) {
        logger.error('Bildirim izni hatası:', error);
      }
    };

    requestPermission();

    // Console'u temizle
    logger.clear();
    logger.log('🚀 SevkiyatDefterim SaaS Uygulaması Başlatıldı');
  }, []);


  const handleTabChange = (tab: TabType) => {
    setCurrentTab(tab);
  };

  const handleAddJob = () => {
    setAddJobDialogOpen(true);
  };

  // Loading durumu
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Uygulama yükleniyor...
        </Typography>
      </Box>
    );
  }

  // Admin kontrolü
  const userIsAdmin = isAdmin(user);

  // Abonelik kontrolü - Admin değilse kontrol et
  const isSubscriptionActive = userIsAdmin || subscriptionStatus === SubscriptionStatus.ACTIVE || subscriptionStatus === SubscriptionStatus.TRIAL;
  const isTeamSubscriptionActive = userIsAdmin || (team ? (teamSubscriptionStatus === SubscriptionStatus.ACTIVE || teamSubscriptionStatus === SubscriptionStatus.TRIAL) : true);

  // SUSPENDED
  if (!userIsAdmin && subscriptionStatus === SubscriptionStatus.SUSPENDED) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: 2,
          p: 3
        }}
      >
        <Typography variant="h4" color="error" gutterBottom>
          Hesabınız Askıya Alındı
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Hesabınız yönetici tarafından askıya alınmıştır. Lütfen destek ile iletişime geçin.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Destek: seliminyazilimhesabi@gmail.com
        </Typography>
      </Box>
    );
  }

  // EXPIRED
  if (!isSubscriptionActive || !isTeamSubscriptionActive) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: 2.5,
          p: 3,
          background: 'linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%)',
        }}
      >
        <Box sx={{ fontSize: 64 }}>
          <LogoutIcon sx={{ fontSize: 64, color: '#ef4444' }} />
        </Box>
        <Typography variant="h4" color="error" fontWeight={800} gutterBottom>
          Deneme Süreniz Doldu
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" maxWidth={400}>
          Devam etmek icin lutfen bizimle iletisime gecin. Size en uygun plani sunalim.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          <Button
            variant="contained"
            color="success"
            size="large"
            startIcon={<WhatsAppIcon />}
            onClick={() => window.open('https://wa.me/905446979787?text=Merhaba%2C%20SevkiyatDefterim%20abonelik%20hakkinda%20bilgi%20almak%20istiyorum.', '_blank')}
            sx={{ textTransform: 'none', borderRadius: 3, fontWeight: 700 }}
          >
            WhatsApp
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<EmailIcon />}
            onClick={() => window.open('mailto:info@atlaslojistik.com.tr?subject=SevkiyatDefterim%20Abonelik', '_blank')}
            sx={{ textTransform: 'none', borderRadius: 3, fontWeight: 700 }}
          >
            E-posta
          </Button>
        </Box>

        <Button
          variant="text"
          color="inherit"
          startIcon={<LogoutIcon />}
          onClick={logout}
          sx={{ mt: 2, textTransform: 'none', color: 'text.secondary' }}
        >
          Çıkış Yap
        </Button>

        <Typography variant="caption" color="text.disabled" sx={{ mt: 1 }}>
          {user?.first_name} - {user?.email || user?.phone_number}
        </Typography>
      </Box>
    );
  }

  // Subscription uyarı banner hesaplama
  const showTrialBanner = !bannerDismissed
    && subscriptionStatus === SubscriptionStatus.TRIAL
    && daysRemaining !== null
    && daysRemaining <= 6;

  const bannerSeverity: 'warning' | 'error' = daysRemaining !== null && daysRemaining <= 2 ? 'error' : 'warning';
  const bannerText = daysRemaining !== null
    ? daysRemaining <= 1
      ? 'Deneme sureniz yarin bitiyor!'
      : `Deneme sureniz ${daysRemaining} gun sonra bitiyor`
    : '';



  const renderCurrentPage = () => {
    switch (currentTab) {
      case 'bugun':
        return <BugunPage onTabChange={handleTabChange} />;
      case 'tamamlanmis':
        return <TamamlanmisPage onTabChange={handleTabChange} />;
      case 'odemeler':
        return <OdemelerPage onTabChange={handleTabChange} />;
      case 'alacaklar':
        return <AlacaklarPage onTabChange={handleTabChange} />;
      case 'komisyon':
        return <KomisyonPage onTabChange={handleTabChange} />;
      case 'fatura':
        return <FaturaPage onTabChange={handleTabChange} />;
      case 'kazanc':
        return <KazancPage onTabChange={handleTabChange} />;
      case 'giderler':
        return <GiderlerPage onTabChange={handleTabChange} />;
      case 'soforler':
        return <SoforlerPage onTabChange={handleTabChange} />;
      case 'fiyatListesi':
        return <FiyatListesiPage onTabChange={handleTabChange} />;
      case 'yetkililer':
        return <YetkililerPage onTabChange={handleTabChange} />;
      case 'firmalar':
        return <FirmalarPage onTabChange={handleTabChange} />;
      case 'kdvHesaplama':
        return <KDVHesaplamaPage onTabChange={handleTabChange} />;
      case 'fisTasarim':
        return <FisTasarimPageNew onTabChange={handleTabChange} />;
      case 'ayarlar':
        return <AyarlarPage onTabChange={handleTabChange} />;
      case 'team':
        return <TeamManagementPage />;
      case 'admin':
        return <AdminPanel />;
      case 'raporlama':
        return <RaporlamaPage />;
      case 'crm':
        return <CRMPage />;
      case 'odemePlan':
        return <OdemePlanPage />;
      case 'whatsapp':
        return <WhatsAppPage />;
      default:
        return <BugunPage onTabChange={handleTabChange} />;
    }
  };

  return (
    <Layout
      currentTab={currentTab}
      onTabChange={handleTabChange}
      onAddJob={handleAddJob}
    >
      {/* Abonelik Uyarı Banner */}
      {showTrialBanner && (
        <Alert
          severity={bannerSeverity}
          sx={{
            borderRadius: 0,
            mx: -1.5,
            mt: -1.5,
            mb: 1.5,
            '& .MuiAlert-message': { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
          }}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button
                color="inherit"
                size="small"
                onClick={() => toast('Yakinda!')}
                sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem' }}
              >
                Plani Yukselt
              </Button>
              <IconButton size="small" color="inherit" onClick={() => setBannerDismissed(true)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          }
        >
          <Typography variant="body2" fontWeight={600}>
            {bannerText}
          </Typography>
        </Alert>
      )}
      <Suspense fallback={<PageLoader />}>{renderCurrentPage()}</Suspense>

      {/* Google/Apple ile giriş yapan kullanıcı telefon numarasını tamamlasın */}
      <Dialog open={needsPhone} disableEscapeKeyDown>
        <DialogTitle>Telefon Numaranızı Girin</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Hesabınızı tamamlamak için telefon numaranızı girin. Bu sayede telefon veya e-posta ile giriş yapabilirsiniz.
          </Typography>
          <TextField
            fullWidth
            label="Telefon Numarası"
            placeholder="05XXXXXXXXX"
            value={phoneInput}
            onChange={e => setPhoneInput(e.target.value)}
            autoFocus
            inputProps={{ maxLength: 13 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={logout} color="inherit">Çıkış</Button>
          <Button
            variant="contained"
            disabled={phoneSubmitting || phoneInput.length < 10}
            onClick={async () => {
              const digits = phoneInput.replace(/\D/g, '');
              const valid = (digits.length === 10 && digits.startsWith('5')) ||
                            (digits.length === 11 && digits.startsWith('05')) ||
                            (digits.length === 12 && digits.startsWith('905'));
              if (!valid) { toast.error('Geçerli bir Türk telefon numarası girin (05XX XXX XX XX)'); return; }
              setPhoneSubmitting(true);
              try { await completePhoneSetup(phoneInput); }
              catch (e: any) { toast.error(e?.message || 'Hata'); }
              finally { setPhoneSubmitting(false); }
            }}
          >
            {phoneSubmitting ? <CircularProgress size={20} /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}


// Authentication wrapper
function AuthWrapper() {
  const { isAuthenticated, isLoading, user, subscriptionStatus } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(() => {
    return !localStorage.getItem('onboarding_done');
  });

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Kimlik doğrulanıyor...
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/quick-setup" element={<QuickSetupPage />} />
          <Route path="/*" element={<AuthPage />} />
        </Routes>
      </Suspense>
    );
  }

  // Onboarding - ilk giriş
  if (showOnboarding) {
    return (
      <Suspense fallback={<PageLoader />}>
        <OnboardingPage
          userName={user?.first_name || 'Kullanıcı'}
          onComplete={() => setShowOnboarding(false)}
        />
      </Suspense>
    );
  }

  return <MainApp />;
}

function App() {
  return (
    <AuthProvider>
      <AppProvider>
        <AppThemeProvider>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#1e293b',
                color: '#f1f5f9',
                borderRadius: '12px',
                fontSize: '0.875rem',
              },
              success: {
                duration: 3000,
                iconTheme: { primary: '#10b981', secondary: '#fff' },
              },
              error: {
                duration: 5000,
                iconTheme: { primary: '#ef4444', secondary: '#fff' },
              },
            }}
          />
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/pdf/:token" element={<PDFViewerPage />} />
                <Route path="/*" element={<AuthWrapper />} />
              </Routes>
            </Suspense>
          </Router>
        </AppThemeProvider>
      </AppProvider>
    </AuthProvider>
  );
}

export default App; 